import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Shield, Landmark, Palmtree, TrendingUp, Globe } from "lucide-react";
import { isRTL } from "@/i18n";

const benefitIcons = [Shield, TrendingUp, Sun, Landmark, Palmtree, Globe];
const benefitKeys = ["tax", "returns", "sun", "ownership", "lifestyle", "location"];

const COASTAL_IMG = "https://images.pexels.com/photos/33841817/pexels-photo-33841817.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function WhyKKTC() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const rtl = isRTL(i18n.language);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-kktc" data-testid="why-kktc-section" ref={sectionRef} className="py-20 md:py-32 px-6 md:px-12 bg-white" dir={rtl ? "rtl" : "ltr"}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <div className={`relative transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : rtl ? "opacity-0 translate-x-16" : "opacity-0 -translate-x-16"}`}>
            <div className="relative rounded-sm overflow-hidden">
              <img src={COASTAL_IMG} alt="KKTC Coast" className="w-full h-[400px] lg:h-[500px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/30 to-transparent" />
            </div>
            <div className={`absolute -bottom-6 ${rtl ? "-left-4 md:left-8" : "-right-4 md:right-8"} bg-[#0A192F] text-white p-6 rounded-sm shadow-2xl`}>
              <p className="text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>12+</p>
              <p className="text-sm text-white/80 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("why_kktc.years_experience")}</p>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : rtl ? "opacity-0 -translate-x-16" : "opacity-0 translate-x-16"}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("why_kktc.pretitle")}
              </span>
            </div>
            <h2 data-testid="why-kktc-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("why_kktc.title_1")}{" "}
              <span className="text-[#D4AF37] italic">{t("why_kktc.title_highlight")}</span>
            </h2>
            <p className="text-[#4A5568] text-base leading-relaxed mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("why_kktc.description")}
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>%15</p>
                  <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("why_kktc.annual_growth")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>50+</p>
                  <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("why_kktc.investors_worldwide")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefitKeys.map((key, i) => {
            const Icon = benefitIcons[i];
            return (
              <div
                key={key}
                data-testid={`benefit-card-${i}`}
                className={`p-8 bg-[#F8F9FA] rounded-sm border border-transparent hover:border-[#D4AF37]/30 hover:shadow-lg transition-all duration-500 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${0.3 + i * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0A192F] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t(`why_kktc.benefits.${key}.title`)}
                </h3>
                <p className="text-sm text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t(`why_kktc.benefits.${key}.desc`)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
